"use client";

import { useRef, useState } from "react";

import { getCurrencySymbol } from "@/features/currency/_logic/iso-4217";

import { useEstimator } from "../../_context/EstimatorContext";
import { useUndoableEdit } from "../../_hooks/useUndoableEdit";
import type { AssumptionField } from "../../_logic/types";

interface AssumptionRateEditProps {
  field: AssumptionField;
  /** Names the rate in labels and the undo toast, e.g. "Meeting space". */
  name: string;
  /** Text after the amount, e.g. "a day, 3 days". */
  suffix: string;
}

const FIELD_INPUT =
  "w-14 rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-right text-xs font-medium tabular-nums text-gray-900 focus:border-indigo-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

/**
 * A per-person daily rate shown under its comparison row ("$25–45 a day"), editable where it
 * stands (dotted underline, hover background): click it for low and high fields, Enter or leaving the fields saves, Escape cancels.
 * Saves go through the workspace reducer with an Undo toast; the chat can change it too.
 * @param props - Which rate, its name and the text after the amount.
 * @returns The rate, or its two fields while editing.
 * @throws Never.
 */
export const AssumptionRateEdit = ({ field, name, suffix }: AssumptionRateEditProps) => {
  // Hooks
  const { workspace, money } = useEstimator();
  const apply = useUndoableEdit();
  const container = useRef<HTMLSpanElement>(null);

  // States
  const [editing, setEditing] = useState(false);
  const [low, setLow] = useState("");
  const [high, setHigh] = useState("");

  // Derived
  const range = workspace.assumptions[field];
  const symbol = getCurrencySymbol(workspace.currency);
  const shown = (usd: number) => String(Math.round(money.fromUsd(usd) ?? usd));
  const byChat = workspace.chatTouched.includes("assumptions");

  // Handlers
  const open = () => {
    setLow(shown(range.low));
    setHigh(shown(range.high));
    setEditing(true);
  };
  const save = () => {
    setEditing(false);
    const typedLow = Number(low.replace(/[^\d.]/g, ""));
    const typedHigh = high.trim() ? Number(high.replace(/[^\d.]/g, "")) : typedLow;
    if (!low.trim() || !Number.isFinite(typedLow) || !Number.isFinite(typedHigh)) return;
    const lowUsd = money.toUsd(Math.min(typedLow, typedHigh));
    const highUsd = money.toUsd(Math.max(typedLow, typedHigh));
    if (lowUsd === null || highUsd === null) return;
    if (Math.round(lowUsd) === Math.round(range.low) && Math.round(highUsd) === Math.round(range.high)) return;
    const next = { low: lowUsd, high: highUsd };
    apply(
      { type: "setAssumptions", assumptions: { [field]: next } },
      `${name} set to ${money.formatRange(next)} a day`
    );
  };
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      save();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setEditing(false);
    }
  };

  // Render
  if (editing) {
    return (
      <span
        ref={container}
        onBlur={(event) => {
          if (!container.current?.contains(event.relatedTarget as Node | null)) save();
        }}
        className="inline-flex flex-wrap items-center gap-1 py-0.5 text-xs font-medium text-gray-500"
      >
        <span>{symbol}</span>
        <input
          autoFocus
          aria-label={`${name}, low per person a day`}
          inputMode="decimal"
          value={low}
          onChange={(event) => setLow(event.target.value)}
          onKeyDown={onKeyDown}
          className={FIELD_INPUT}
        />
        <span>–</span>
        <span>{symbol}</span>
        <input
          aria-label={`${name}, high per person a day`}
          inputMode="decimal"
          value={high}
          onChange={(event) => setHigh(event.target.value)}
          onKeyDown={onKeyDown}
          className={FIELD_INPUT}
        />
        <span>{suffix}</span>
      </span>
    );
  }

  return (
    // ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit
    <button
      type="button"
      onClick={open}
      aria-label={`Edit ${name.toLowerCase()} per person a day, now ${money.formatRange(range)}`}
      title="Edit the rate"
      data-tour="estimator-rates"
      className="group/rate -mx-1 inline-flex items-center gap-1 rounded px-1 text-left hover:bg-gray-100 text-xs font-medium text-gray-500 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <span>
        <span
          className={`underline decoration-dotted decoration-gray-400 underline-offset-[3px] group-hover/rate:decoration-gray-700 ${byChat ? "text-indigo-700" : ""}`}
        >
          {money.formatRange(range)}
        </span>{" "}
        {suffix}
      </span>
    </button>
  );
};
